import * as THREE from 'three';

export function createDNAHelix(canvasElement, dnaColor, highlightColor) {
  const scene = new THREE.Scene();

  const renderer = new THREE.WebGLRenderer({
    canvas: canvasElement,
    alpha: true,
    context: canvasElement.getContext("webgl", { preserveDrawingBuffer: true }),
  });

  renderer.setPixelRatio(window.devicePixelRatio);

  const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
  camera.position.set(35, 0, 0);
  camera.lookAt(0, 0, 0);

  function resizeRenderer() {
    const bounds = canvasElement.getBoundingClientRect();
    renderer.setSize(bounds.width, bounds.height, false);
    camera.aspect = bounds.width / bounds.height;
    camera.updateProjectionMatrix();
  }

  resizeRenderer();
  setTimeout(resizeRenderer, 100);
  window.addEventListener('resize', resizeRenderer);

  // Lights
  scene.add(new THREE.AmbientLight(0xffffff, 0.1));

  const pointLight = new THREE.PointLight(0xffffff, 50000);
  pointLight.position.set(20, 60, 23);
  scene.add(pointLight);

  const coloredLight = new THREE.PointLight(highlightColor, 40000);
  coloredLight.position.set(-15, 38, -28);
  scene.add(coloredLight);

  // DNA Parts
  const dna = new THREE.Object3D();
  const backboneMaterial = new THREE.MeshPhongMaterial({ color: dnaColor });

  const basePairsPerTurn = 10.5;
  const angleStep = (360 / (basePairsPerTurn * 2)) * Math.PI / 180;
  const risePerBasePair = 2;
  const i_max = 20;

  const basePairGeometry = new THREE.CylinderGeometry(0.3, 0.3, 6, 32);
  const connectionGeometry = new THREE.CylinderGeometry(0.3, 0.3, 2.5, 32);
  const backboneGeometry = new THREE.SphereGeometry(0.8, 32, 32);

  const colors = [dnaColor, dnaColor, dnaColor, dnaColor];

  const getBasePairMaterial = (i) =>
    new THREE.MeshPhongMaterial({ color: colors[i % colors.length] });

  for (let i = 0; i <= i_max; i++) {
    const rc_i = Math.floor(Math.random() * 4);

    const basePairRight = new THREE.Mesh(basePairGeometry, getBasePairMaterial(rc_i));
    basePairRight.rotation.z = Math.PI / 2;
    basePairRight.position.x = -3;

    const basePairLeft = new THREE.Mesh(
      basePairGeometry,
      getBasePairMaterial(rc_i >= 2 ? rc_i - 2 : rc_i + 2)
    );
    basePairLeft.rotation.z = Math.PI / 2;
    basePairLeft.position.x = 3;

    const ballRight = new THREE.Mesh(backboneGeometry, backboneMaterial);
    ballRight.position.x = 6;

    const ballLeft = new THREE.Mesh(backboneGeometry, backboneMaterial);
    ballLeft.position.x = -6;

    const row = new THREE.Object3D();
    row.add(basePairRight, basePairLeft, ballRight, ballLeft);

    if (i < i_max) {
      const connL = new THREE.Mesh(connectionGeometry, backboneMaterial);
      connL.position.set(5.9, 1.3, -1);
      connL.rotation.set(THREE.MathUtils.degToRad(-45), 0, THREE.MathUtils.degToRad(-5));

      const connR = new THREE.Mesh(connectionGeometry, backboneMaterial);
      connR.position.set(-5.9, 1.3, 1);
      connR.rotation.set(THREE.MathUtils.degToRad(45), 0, THREE.MathUtils.degToRad(5));

      row.add(connL, connR);
    }

    row.position.y = i * risePerBasePair;
    row.rotation.y = angleStep * i;

    dna.add(row);
  }

  dna.position.y = -20;
  scene.add(dna);

  let animationId = null;
  let isActive = false;

  function animate() {
    if (!isActive) return;

    animationId = requestAnimationFrame(animate);
    dna.rotation.y -= 0.01;
    renderer.render(scene, camera);
  }

  canvasElement.__threeResize = resizeRenderer;

  // Return controller object
  return {
    start: () => {
      if (!isActive) {
        isActive = true;
        animate();
      }
    },
    pause: () => {
      isActive = false;
      if (animationId) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }
    },
    render: () => {
      if (isActive) {
        renderer.render(scene, camera);
      }
    }
  };
}
